<template>
  <div class="project-assessment-dashboard">
    <header class="dashboard-header">
      <div class="header-content">
        <div class="logo-section">
          <div class="kpmg-logo" @click="navigateToHome">
            <img src="/kpmg-logo-blue.svg" alt="KPMG" class="kpmg-logo-img" />
          </div>
          <h1>Project Assessment Dashboard</h1>
        </div>
        <button @click="goBack" class="back-btn">← Back to Dashboard</button>
      </div>
    </header>

    <main class="main-content">
      <div class="container">
        
        <!-- Overall Score Card -->
        <div class="dashboard-card overall-score-card">
          <div class="card-header">
            <h2>Project Overall Score</h2>
            <div class="project-selector">
              <select v-model="selectedProjectId" @change="onProjectChange" class="project-dropdown">
                <option value="">Select a project...</option>
                <option v-for="project in availableProjects" :key="project.id" :value="project.id">
                  {{ project.name }}
                </option>
              </select>
            </div>
          </div>
          
          <div v-if="selectedProject" class="card-content">
            <div class="rating-section">
              <div class="rating-label" :class="overallRatingClass">
                {{ overallRatingText }}
              </div>
              <div class="score-section">
                <div class="score-circle">
                  <div class="circle-progress" :style="{ background: `conic-gradient(#0056b3 ${overallScorePercentage}%, #e0e0e0 ${overallScorePercentage}%)` }">
                    <div class="circle-inner">
                      <span class="score-value">{{ overallScore.toFixed(1) }}</span>
                      <span class="score-max">/ 5.0</span>
                    </div>
                  </div>
                </div>
                <div class="legend">
                  <div class="legend-item">
                    <span class="legend-dot excellent"></span>
                    <span>Excellent (4.0-5.0)</span>
                  </div>
                  <div class="legend-item">
                    <span class="legend-dot good"></span>
                    <span>Good (3.0-3.9)</span>
                  </div>
                  <div class="legend-item">
                    <span class="legend-dot adequate"></span>
                    <span>Adequate (2.0-2.9)</span>
                  </div>
                  <div class="legend-item">
                    <span class="legend-dot poor"></span>
                    <span>Poor (0-1.9)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="no-project-selected">
            <p>Please select a project to view assessment details</p>
          </div>
        </div>

        <!-- Assessment Section Cards -->
        <div v-if="selectedProject" class="assessment-grid">
          <div v-for="section in assessmentSections" :key="section.id" class="dashboard-card section-card">
            <div class="card-header">
              <h3>{{ section.name }}</h3>
              <div class="weight-label">Weight: {{ section.weight }}%</div>
            </div>
            
            <div class="card-content">
              <div class="section-score">
                <div class="score-circle small">
                  <div class="circle-progress" :style="{ background: `conic-gradient(#0056b3 ${getSectionScorePercentage(section.score)}%, #e0e0e0 ${getSectionScorePercentage(section.score)}%)` }">
                    <div class="circle-inner">
                      <span class="score-value">{{ section.score.toFixed(1) }}</span>
                      <span class="score-max">/ 5.0</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="sub-parameters">
                <div v-for="param in section.subParameters" :key="param.name" class="parameter-bar">
                  <div class="parameter-info">
                    <span class="parameter-name">{{ param.name }}</span>
                    <span class="parameter-score">{{ param.score.toFixed(1) }} ({{ param.weight }}%)</span>
                  </div>
                  <div class="progress-bar">
                    <div class="progress-fill" :class="getParameterClass(param.score)" :style="{ width: getParameterPercentage(param.score) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'ProjectAssessmentDashboard',
  data() {
    return {
      selectedProjectId: '',
      availableProjects: [
        {
          id: 1,
          name: 'Metro Line Extension Project',
          sector: 'Transport',
          stage: 'Implementation',
          totalCost: 450,
          implementingAgency: 'Department of Transportation',
          timeline: 48,
          fundingSource: 'PPP'
        },
        {
          id: 2,
          name: 'Water Treatment Facility',
          sector: 'Water',
          stage: 'Feasibility',
          totalCost: 280,
          implementingAgency: 'Ministry of Water Resources',
          timeline: 36,
          fundingSource: 'Government Budget'
        },
        {
          id: 3,
          name: 'Solar Power Plant',
          sector: 'Energy',
          stage: 'Concept',
          totalCost: 320,
          implementingAgency: 'Ministry of Energy',
          timeline: 42,
          fundingSource: 'International Loan'
        },
        {
          id: 4,
          name: 'Hospital Infrastructure',
          sector: 'Health',
          stage: 'Pre-feasibility',
          totalCost: 180,
          implementingAgency: 'Ministry of Health',
          timeline: 30,
          fundingSource: 'Grant'
        },
        {
          id: 5,
          name: 'Smart City Initiative',
          sector: 'Urban infrastructure',
          stage: 'Procurement',
          totalCost: 520,
          implementingAgency: 'Ministry of Urban Development',
          timeline: 60,
          fundingSource: 'PPP'
        }
      ]
    }
  },
  computed: {
    selectedProject() {
      return this.availableProjects.find(p => p.id == this.selectedProjectId)
    },
    overallScore() {
      if (!this.selectedProject) return 0
      const sections = this.assessmentSections
      const weightedSum = sections.reduce((sum, section) => sum + (section.score * section.weight / 100), 0)
      return weightedSum
    },
    overallScorePercentage() {
      return (this.overallScore / 5) * 100
    },
    overallRatingText() {
      const score = this.overallScore
      if (score >= 4.0) return 'Excellent'
      if (score >= 3.0) return 'Good'
      if (score >= 2.0) return 'Moderately Adequate'
      return 'Poor'
    },
    overallRatingClass() {
      const score = this.overallScore
      if (score >= 4.0) return 'excellent'
      if (score >= 3.0) return 'good'
      if (score >= 2.0) return 'adequate'
      return 'poor'
    },
    assessmentSections() {
      if (!this.selectedProject) return []
      
      return [
        {
          id: 'strategic-suitability',
          name: 'Strategic Suitability',
          weight: 20,
          score: this.generateScore(3.8, 0.5),
          subParameters: [
            { name: 'Alignment with priorities', score: this.generateScore(4.2, 0.3), weight: 40 },
            { name: 'Economic impact', score: this.generateScore(3.5, 0.4), weight: 35 },
            { name: 'Social benefit', score: this.generateScore(3.7, 0.3), weight: 25 }
          ]
        },
        {
          id: 'preliminary-feasibility',
          name: 'Preliminary Feasibility',
          weight: 15,
          score: this.generateScore(3.2, 0.6),
          subParameters: [
            { name: 'Technical feasibility', score: this.generateScore(3.8, 0.4), weight: 40 },
            { name: 'Financial viability', score: this.generateScore(2.9, 0.5), weight: 35 },
            { name: 'Market demand', score: this.generateScore(3.0, 0.4), weight: 25 }
          ]
        },
        {
          id: 'risk-assessment',
          name: 'Risk Assessment',
          weight: 15,
          score: this.generateScore(2.8, 0.7),
          subParameters: [
            { name: 'Construction risk', score: this.generateScore(2.5, 0.6), weight: 30 },
            { name: 'Operational risk', score: this.generateScore(3.1, 0.5), weight: 25 },
            { name: 'Financial risk', score: this.generateScore(2.7, 0.7), weight: 25 },
            { name: 'Political risk', score: this.generateScore(3.0, 0.6), weight: 20 }
          ]
        },
        {
          id: 'ppp-suitability',
          name: 'PPP Suitability',
          weight: 12,
          score: this.generateScore(3.5, 0.6),
          subParameters: [
            { name: 'Revenue model', score: this.generateScore(3.8, 0.4), weight: 30 },
            { name: 'Risk transfer potential', score: this.generateScore(3.3, 0.5), weight: 30 },
            { name: 'Private sector interest', score: this.generateScore(3.4, 0.6), weight: 25 },
            { name: 'Contract duration', score: this.generateScore(3.6, 0.4), weight: 15 }
          ]
        },
        {
          id: 'fiscal-affordability',
          name: 'Fiscal Affordability',
          weight: 10,
          score: this.generateScore(3.0, 0.8),
          subParameters: [
            { name: 'Budget availability', score: this.generateScore(2.8, 0.7), weight: 40 },
            { name: 'Debt capacity', score: this.generateScore(3.2, 0.6), weight: 35 },
            { name: 'Fiscal impact', score: this.generateScore(3.0, 0.8), weight: 25 }
          ]
        },
        {
          id: 'institutional-capability',
          name: 'Institutional Capability',
          weight: 10,
          score: this.generateScore(3.3, 0.5),
          subParameters: [
            { name: 'Management capacity', score: this.generateScore(3.5, 0.4), weight: 35 },
            { name: 'Technical expertise', score: this.generateScore(3.2, 0.6), weight: 30 },
            { name: 'Procurement experience', score: this.generateScore(3.1, 0.5), weight: 35 }
          ]
        },
        {
          id: 'climate-risk',
          name: 'Climate Risk Exposure',
          weight: 8,
          score: this.generateScore(2.7, 0.9),
          subParameters: [
            { name: 'Physical risk exposure', score: this.generateScore(2.4, 0.8), weight: 50 },
            { name: 'Adaptation measures', score: this.generateScore(3.0, 0.7), weight: 30 },
            { name: 'Resilience planning', score: this.generateScore(2.8, 0.9), weight: 20 }
          ]
        },
        {
          id: 'climate-smart-ppp',
          name: 'Climate-Smart PPP',
          weight: 10,
          score: this.generateScore(3.6, 0.4),
          subParameters: [
            { name: 'Green innovation', score: this.generateScore(3.8, 0.3), weight: 40 },
            { name: 'Sustainability metrics', score: this.generateScore(3.5, 0.5), weight: 35 },
            { name: 'Environmental compliance', score: this.generateScore(3.5, 0.4), weight: 25 }
          ]
        }
      ]
    }
  },
  methods: {
    generateScore(baseScore, variance) {
      const projectFactor = this.selectedProjectId ? (this.selectedProjectId * 0.1) % 1 : 0.5
      const variation = (Math.random() - 0.5) * variance
      const score = Math.max(0, Math.min(5, baseScore + variation + (projectFactor - 0.5) * 0.8))
      return score
    },
    onProjectChange() {
      // Trigger reactivity to update scores for the new project
      this.$forceUpdate()
    },
    getSectionScorePercentage(score) {
      return (score / 5) * 100
    },
    getParameterPercentage(score) {
      return (score / 5) * 100
    },
    getParameterClass(score) {
      if (score >= 4.0) return 'excellent'
      if (score >= 3.0) return 'good'
      if (score >= 2.0) return 'adequate'
      return 'poor'
    },
    goBack() {
      this.$emit('navigate', 'dashboard')
    },
    navigateToHome() {
      this.$emit('navigate', 'landing')
    }
  }
}
</script>

<style scoped>
.project-assessment-dashboard {
  min-height: 100vh;
  background: rgb(229, 229, 229);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.dashboard-header {
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.kpmg-logo {
  background: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.kpmg-logo:hover {
  transform: scale(1.05);
}

.kpmg-logo-img {
  height: 32px;
  width: auto;
}

.logo-section h1 {
  font-size: 1.8rem;
  color: #0056b3;
  margin: 0;
  font-weight: 600;
}

.back-btn {
  background: transparent;
  color: #0056b3;
  border: 1px solid #0056b3;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.back-btn:hover {
  background: #0056b3;
  color: white;
}

.main-content {
  padding: 2rem 0;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
}

/* Dashboard Cards */
.dashboard-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  margin-bottom: 2rem;
  transition: transform 0.3s, box-shadow 0.3s;
}

.dashboard-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.12);
}

.card-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h2 {
  font-size: 1.5rem;
  color: #0056b3;
  margin: 0;
  font-weight: 600;
}

.card-header h3 {
  font-size: 1.2rem;
  color: #0056b3;
  margin: 0;
  font-weight: 600;
}

.card-content {
  padding: 2rem;
}

/* Project Selector */
.project-selector {
  min-width: 300px;
}

.project-dropdown {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s;
}

.project-dropdown:focus {
  outline: none;
  border-color: #0056b3;
}

/* Overall Score Card */
.overall-score-card {
  margin-bottom: 3rem;
}

.rating-section {
  display: flex;
  align-items: center;
  gap: 3rem;
}

.rating-label {
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  font-size: 1.1rem;
  color: white;
}

.rating-label.excellent {
  background: #28a745;
}

.rating-label.good {
  background: #0056b3;
}

.rating-label.adequate {
  background: #0056b3;
}

.rating-label.poor {
  background: #dc3545;
}

.score-section {
  display: flex;
  align-items: center;
  gap: 2rem;
  flex: 1;
}

/* Circular Progress */
.score-circle {
  position: relative;
  width: 120px;
  height: 120px;
}

.score-circle.small {
  width: 80px;
  height: 80px;
}

.circle-progress {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.6s ease;
}

.circle-inner {
  width: 85%;
  height: 85%;
  background: white;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.score-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #0056b3;
  line-height: 1;
}

.score-circle.small .score-value {
  font-size: 1.2rem;
}

.score-max {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

.score-circle.small .score-max {
  font-size: 0.7rem;
}

/* Legend */
.legend {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #666;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.legend-dot.excellent {
  background: #28a745;
}

.legend-dot.good {
  background: #0056b3;
}

.legend-dot.adequate {
  background: #ffc107;
}

.legend-dot.poor {
  background: #dc3545;
}

/* Assessment Grid */
.assessment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
}

.section-card .card-content {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
}

.section-score {
  flex-shrink: 0;
}

.sub-parameters {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.weight-label {
  background: #f8f9fa;
  color: #666;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Parameter Bars */
.parameter-bar {
  margin-bottom: 0.75rem;
}

.parameter-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.parameter-name {
  font-size: 0.9rem;
  color: #333;
  font-weight: 500;
}

.parameter-score {
  font-size: 0.8rem;
  color: #666;
  font-weight: 600;
}

.progress-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
}

.progress-fill.excellent {
  background: #28a745;
}

.progress-fill.good {
  background: #0056b3;
}

.progress-fill.adequate {
  background: #ffc107;
}

.progress-fill.poor {
  background: #dc3545;
}

/* No Project Selected */
.no-project-selected {
  text-align: center;
  padding: 3rem;
  color: #666;
  font-size: 1.1rem;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .assessment-grid {
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .rating-section {
    flex-direction: column;
    gap: 2rem;
    text-align: center;
  }
  
  .score-section {
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .section-card .card-content {
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .assessment-grid {
    grid-template-columns: 1fr;
  }
  
  .project-selector {
    min-width: 250px;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 1rem;
  }
  
  .card-header {
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .card-content {
    padding: 1rem;
  }
}
</style>